"use client";

import * as React from "react";
import { flexRender } from "@tanstack/react-table";
import type { Header, Table } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { cn } from "@aio/platform-ui";

/**
 * Props for {@link TableHeader}.
 *
 * The header is rendered against a fully-configured TanStack Table instance —
 * it never reaches into raw {@link import("../columns").ColumnDef} state, which
 * keeps it composable with any caller that has already wired
 * `useReactTable(...)` (the `<DataTable>` composer in Phase G).
 */
export interface TableHeaderProps<TData> {
  /** A configured TanStack Table instance. */
  table: Table<TData>;
  /**
   * Optional slot rendered to the right of the header label, before the
   * resize handle. The `<DataTable>` composer will pass the per-column
   * dropdown trigger here once Phase G wires the menu in.
   */
  renderColumnAction?: (header: Header<TData, unknown>) => React.ReactNode;
  /** Extra classes for the `<thead>` element. */
  className?: string;
}

/**
 * Maps a TanStack header to the appropriate sort glyph.
 *
 * - asc  -> ArrowUp
 * - desc -> ArrowDown
 * - none + sortable -> ChevronsUpDown (subtle hint)
 * - non-sortable -> null
 */
function SortGlyph<TData>({ header }: { header: Header<TData, unknown> }) {
  if (!header.column.getCanSort()) return null;
  const dir = header.column.getIsSorted();
  if (dir === "asc") {
    return <ArrowUp className="size-3.5 shrink-0" aria-hidden="true" />;
  }
  if (dir === "desc") {
    return <ArrowDown className="size-3.5 shrink-0" aria-hidden="true" />;
  }
  return (
    <ChevronsUpDown
      className="size-3.5 shrink-0 text-fg-muted opacity-60"
      aria-hidden="true"
    />
  );
}

/**
 * Translate the column's `meta.align` (or numeric-type heuristic) to the
 * Tailwind class set used to align both the cell text and the header label.
 */
function alignClass(align: "left" | "center" | "right" | undefined): string {
  if (align === "right") return "justify-end text-right";
  if (align === "center") return "justify-center text-center";
  return "justify-start text-left";
}

/**
 * Sticky header for a TanStack Table-backed datagrid.
 *
 * Behaviour:
 * - Renders one `<th>` per visible leaf column.
 * - Click on the header label toggles sorting (asc -> desc -> none) when
 *   `column.getCanSort()` is true. We dispatch via TanStack's own
 *   `getToggleSortingHandler` so the engine's enableSortingRemoval semantics
 *   are honoured.
 * - A drag handle on the right edge calls `header.getResizeHandler()` for both
 *   pointer and touch input, matching TanStack's documented contract.
 * - The current sort direction is shown via a lucide chevron (asc / desc) or
 *   a muted up-down glyph (sortable but unsorted). Non-sortable columns get
 *   no glyph at all.
 *
 * The component only touches presentation; the table engine owns state.
 */
export function TableHeader<TData>({
  table,
  renderColumnAction,
  className,
}: TableHeaderProps<TData>): React.JSX.Element {
  return (
    <thead
      className={cn(
        "sticky top-0 z-10 bg-bg-elevated text-xs font-semibold text-fg-muted",
        className,
      )}
    >
      {table.getHeaderGroups().map((group) => (
        <tr key={group.id} className="border-b border-border">
          {group.headers.map((header) => {
            const canSort = header.column.getCanSort();
            const canResize = header.column.getCanResize();
            const isResizing = header.column.getIsResizing();
            const align = header.column.columnDef.meta as
              | { align?: "left" | "center" | "right" }
              | undefined;
            const sortHandler = header.column.getToggleSortingHandler();
            const sortDir = header.column.getIsSorted();
            const ariaSort: "ascending" | "descending" | "none" =
              sortDir === "asc"
                ? "ascending"
                : sortDir === "desc"
                  ? "descending"
                  : "none";

            return (
              <th
                key={header.id}
                colSpan={header.colSpan}
                scope="col"
                style={{ width: header.getSize() }}
                aria-sort={canSort ? ariaSort : undefined}
                className={cn(
                  "relative select-none px-3 py-2 align-middle",
                  // Reserve space on the right edge so the resize handle never
                  // overlaps the action slot or the sort glyph.
                  canResize ? "pr-4" : undefined,
                )}
              >
                <div
                  className={cn(
                    "flex items-center gap-1.5",
                    alignClass(align?.align),
                  )}
                >
                  {canSort ? (
                    <button
                      type="button"
                      onClick={sortHandler}
                      onKeyDown={(e) => {
                        // Match native <th>+button: Space toggles too. Enter
                        // is handled by the browser on <button>.
                        if (e.key === " ") {
                          e.preventDefault();
                          sortHandler?.(e);
                        }
                      }}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-sm",
                        "outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-bg-elevated",
                        "transition-colors duration-fast ease-standard",
                        "hover:text-fg",
                      )}
                      data-testid={`th-sort-${header.column.id}`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      <SortGlyph header={header} />
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </span>
                  )}

                  {renderColumnAction ? (
                    <span className="ml-auto inline-flex items-center">
                      {renderColumnAction(header)}
                    </span>
                  ) : null}
                </div>

                {canResize ? (
                  <span
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize column ${header.column.id}`}
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    onClick={(e) => {
                      // The drag handle must never bubble to the sort button.
                      e.stopPropagation();
                    }}
                    className={cn(
                      "absolute right-0 top-0 h-full w-1 cursor-col-resize",
                      "select-none touch-none",
                      "bg-transparent hover:bg-border-strong",
                      isResizing ? "bg-accent" : undefined,
                    )}
                    data-testid={`th-resize-${header.column.id}`}
                  />
                ) : null}
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}
